// First-time password setup for freshly-invited users.
//
// Reached when user.user_metadata.force_password_change is true — the
// invite flow (admin invites client / client invites team member) sets
// this flag on the backend so the magic-link sign-in lands here rather
// than dropping the user into the dashboard with no password set.
//
// On save:
//   1. supabase.auth.updateUser({ password }) sets the new password.
//   2. POST /me/complete-first-login asks the backend to clear the
//      force_password_change flag in BOTH app_metadata and user_metadata
//      via the admin API. user_metadata is client-writable so clearing
//      it here is meaningless — the guard reads app_metadata (which the
//      client cannot touch) so the backend has to do it.
//   3. supabase.auth.refreshSession() so the local session picks up
//      the cleared app_metadata before RootRedirect re-evaluates.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Lock, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { completeFirstLogin } from '../lib/api'

const MIN_LEN = 8

export default function SetPassword() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState(null)
  const [success,  setSuccess]  = useState(false)

  // Booting up — show a centered spinner so we don't briefly flash the
  // form before the auth context settles.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 size={24} className="text-blue-600 animate-spin" />
      </div>
    )
  }

  // No session — most commonly reached when an invite link is expired
  // (Supabase's default TTL is a few hours) or has been clicked more
  // than once. Give a clearer path than a bare "Go to sign-in" button,
  // which today loops the user back to a login they can't complete.
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
          <div className="px-8 pt-8 pb-6 border-b border-slate-100 text-center">
            <img src="/logo.png" alt="OPS 3PL" className="h-12 mx-auto mb-5" />
            <h1 className="text-xl font-semibold text-slate-800">Invite link expired</h1>
            <p className="text-sm text-slate-500 mt-2">
              Your invite link couldn't sign you in — it may have expired
              or already been used.
            </p>
          </div>
          <div className="px-8 py-6 text-sm text-slate-600 space-y-3">
            <p>
              Reach out to your account manager at{' '}
              <a href="mailto:steven@opsconsulting.com.au" className="text-blue-600 hover:underline">
                steven@opsconsulting.com.au
              </a>{' '}
              to have a fresh invite sent.
            </p>
            <p className="text-xs text-slate-400">
              If you already set your password, sign in normally instead.
            </p>
          </div>
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex justify-center">
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="text-sm text-blue-600 hover:underline"
            >
              Go to sign-in
            </button>
          </div>
        </div>
      </div>
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (password.length < MIN_LEN) {
      return setError(`Password must be at least ${MIN_LEN} characters.`)
    }
    if (password !== confirm) {
      return setError("Passwords don't match.")
    }
    setSaving(true)
    try {
      // (1) Set the new password. We deliberately do NOT pass
      //     `data: { force_password_change: false }` here — user_metadata
      //     is client-writable and clearing it locally is meaningless
      //     because the guard reads the flag from app_metadata, which
      //     only the backend admin API can mutate.
      const { error: upErr } = await supabase.auth.updateUser({ password })
      if (upErr) throw upErr

      // (2) Ask the backend to clear force_password_change in
      //     app_metadata (and user_metadata, for tidiness) via the
      //     admin API. If this fails the guard will bounce the user
      //     right back here on the next render — surface the error
      //     rather than pretending success.
      await completeFirstLogin()

      setSuccess(true)
      // (3) Refresh the session so the local user picks up the cleared
      //     app_metadata flag before RootRedirect re-evaluates.
      //     Without this, the route guard could ping-pong us straight
      //     back here.
      await supabase.auth.refreshSession()
      // Tiny pause so the user actually sees the success state.
      setTimeout(() => navigate('/', { replace: true }), 800)
    } catch (err) {
      setError(err?.message || 'Failed to set password. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const firstName = (user.user_metadata?.full_name || '').split(' ')[0]

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-slate-100 text-center">
            <img
              src="/logo.png"
              alt="OPS 3PL"
              className="h-12 mx-auto mb-5"
            />
            <h1 className="text-xl font-semibold text-slate-800">
              {firstName ? `Welcome, ${firstName}` : 'Welcome to OPS 3PL'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Set a password to finish setting up your account.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                New password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={`At least ${MIN_LEN} characters`}
                  autoFocus
                  required
                  className="input pl-9 pr-9 w-full text-sm"
                  disabled={saving || success}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  required
                  className="input pl-9 w-full text-sm"
                  disabled={saving || success}
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
                <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
                <span>Password set. Taking you to your dashboard…</span>
              </div>
            )}

            <button
              type="submit"
              disabled={saving || success || password.length < MIN_LEN || password !== confirm}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                : 'Save password'}
            </button>
          </form>

          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100">
            <p className="text-xs text-slate-500 text-center">
              You can change this anytime from <strong>Account → Security</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
